import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ENGINE_LABELS,
  PERSONA_LABELS,
  type Engine,
  type Persona,
} from "@velivo/shared";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import { DataTable, type Column } from "../components/DataTable";
import { SidePanel } from "../components/SidePanel";
import { Tabs } from "../components/Tabs";
import { Icon } from "../components/Icon";
import "./dashboard.css";
import "./drift.css";
import "./captures.css";

/**
 * PRODUCT_FLOW.md Stage 4 §2 — Run History.
 *
 * The API has served this since Phase 3, but nothing in the UI reached it,
 * which meant FR-CAP-01's acceptance criterion ("a run's status per engine is
 * visible, with per-engine error detail on failure") was satisfied by the
 * server and invisible to the user.
 *
 * Partial failure is the state this screen exists for. Stage 4 §2 is explicit:
 * "if Copilot's capture worker breaks because of a UI change on Microsoft's
 * end, the other engines' results should still land and score normally, with
 * Copilot's failure clearly flagged rather than blanking the whole run." So
 * every engine gets its own chip, and a failure carries its reason.
 */

type RunStatus =
  "queued" | "running" | "completed" | "partial_failure" | "failed";

interface EngineOutcome {
  engine: Engine;
  status: "success" | "failed";
  failureReason: string | null;
}

interface Run {
  runId: string;
  pillarId: string;
  persona: Persona;
  promptText: string;
  triggeredBy: "manual" | "scheduled" | "retest";
  status: RunStatus;
  requestedAt: string;
  completedAt: string | null;
  engines: EngineOutcome[];
}

interface ResponseRow {
  responseId: string;
  engine: Engine;
  status: "success" | "failed";
  failureReason: string | null;
  rawText: string | null;
  citations: { citationId: string; url: string | null; sourceName: string }[];
}

const STATUS_LABELS: Record<RunStatus, string> = {
  queued: "Queued",
  running: "Running",
  completed: "All engines succeeded",
  partial_failure: "Some engines failed",
  failed: "All engines failed",
};

const STATUS_BADGE: Record<RunStatus, string> = {
  queued: "badge--neutral",
  running: "badge--info",
  completed: "badge--aligned",
  partial_failure: "badge--erosion",
  failed: "badge--critical",
};

const TRIGGER_LABELS: Record<Run["triggeredBy"], string> = {
  manual: "Manual",
  scheduled: "Scheduled",
  retest: "Verification re-test",
};

export function CapturesPage() {
  const { activeBrand, can } = useAuth();
  const brandId = activeBrand?.brandId ?? null;
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [pillars, setPillars] = useState<{ pillarId: string; name: string }[]>(
    [],
  );
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const data = await api<{ items: Run[] }>(
        `/brands/${brandId}/runs?limit=100`,
      );
      setRuns(data.items);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not load run history.",
      );
      setRuns([]);
    }
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A run in flight finishes on its own; refresh rather than making the user
  // reload to find out whether their capture landed.
  useEffect(() => {
    if (!runs?.some((r) => r.status === "queued" || r.status === "running"))
      return;
    const t = setTimeout(() => void load(), 3000);
    return () => clearTimeout(t);
  }, [runs, load]);

  useEffect(() => {
    if (!brandId) return;
    void api<{ items: { pillarId: string; name: string }[] }>(
      `/brands/${brandId}/pillars`,
    )
      .then((d) => setPillars(d.items))
      .catch(() => setPillars([]));
  }, [brandId]);

  const pillarName = useMemo(() => {
    const map = new Map(pillars.map((p) => [p.pillarId, p.name]));
    return (id: string) => map.get(id) ?? "Unknown pillar";
  }, [pillars]);

  const columns: Column<Run>[] = [
    {
      key: "requestedAt",
      header: "Run",
      width: "180px",
      value: (r) => r.requestedAt,
      render: (r) => (
        <>
          <div>{new Date(r.requestedAt).toLocaleString()}</div>
          <div className="muted">{TRIGGER_LABELS[r.triggeredBy]}</div>
        </>
      ),
    },
    {
      key: "prompt",
      header: "Prompt",
      value: (r) => `${r.promptText} ${pillarName(r.pillarId)}`,
      render: (r) => (
        <>
          <div className="drift__desc">{r.promptText}</div>
          <div className="muted">
            {pillarName(r.pillarId)} · {PERSONA_LABELS[r.persona] ?? r.persona}
          </div>
        </>
      ),
    },
    {
      key: "status",
      header: "Outcome",
      width: "190px",
      value: (r) => r.status,
      render: (r) => (
        <span className={`badge ${STATUS_BADGE[r.status]}`}>
          {STATUS_LABELS[r.status]}
        </span>
      ),
    },
    {
      key: "engines",
      header: "Engines",
      width: "230px",
      value: (r) => r.engines.map((e) => `${e.engine}:${e.status}`).join(" "),
      render: (r) => (
        <span className="enginechips">
          {r.engines.length === 0 ? (
            <span className="muted">no results yet</span>
          ) : (
            r.engines.map((e) => (
              <span
                key={e.engine}
                className={`enginechip enginechip--${e.status}`}
                // The reason travels with the chip, so diagnosing a partial
                // failure doesn't require opening the run.
                title={
                  e.status === "failed"
                    ? (e.failureReason ?? "Capture failed.")
                    : `${ENGINE_LABELS[e.engine] ?? e.engine} succeeded`
                }
              >
                {(ENGINE_LABELS[e.engine] ?? e.engine).slice(0, 3)}
              </span>
            ))
          )}
        </span>
      ),
    },
    {
      key: "open",
      header: "",
      width: "110px",
      render: (r) => (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setOpenRunId(r.runId)}
        >
          Open <Icon name="arrowRight" />
        </button>
      ),
    },
  ];

  return (
    <div className="stack">
      <header className="page__header">
        <h1 className="page__title">Captures</h1>
        <p className="page__subtitle">
          Every run of every approved prompt, with each engine&rsquo;s outcome.
          A failed engine is recorded with its reason — it never silently
          disappears from a run.
        </p>
      </header>

      {error ? <div className="alert alert--error">{error}</div> : null}

      {can("brand:approve_prompts") ? (
        <RunScan brandId={brandId} pillars={pillars} onStarted={load} />
      ) : null}

      <DataTable
        rows={runs}
        columns={columns}
        rowKey={(r) => r.runId}
        searchPlaceholder="Search by prompt, pillar, or persona…"
        empty={{
          icon: "clock",
          title: "No captures yet",
          description:
            "Approve a prompt and run it to ask the engines what they say about this Brand. Results land here with each engine’s status.",
        }}
      />

      {openRunId ? (
        <RunPanel
          runId={openRunId}
          pillarName={pillarName}
          onClose={() => setOpenRunId(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The run-detail endpoint returns the run itself, not the per-engine roll-up
 * the list endpoint computes — so this is typed for what actually arrives
 * rather than reusing `Run` and quietly relying on a field that is never sent.
 */
type RunDetail = Omit<Run, "engines"> & { requestedEngines: Engine[] };

/**
 * FR-CAP-02 — trigger a run for a whole pillar, which is how a row of the
 * Narrative Alignment Matrix gets filled.
 *
 * The cost is shown BEFORE the click, not after. Each prompt × engine pair is
 * one billable capture and one scoring pass, so an unbounded "scan everything"
 * button is a way to spend real money by accident (NFR.md §7). The default is
 * deliberately small and spread across personas.
 */
interface ScanPreview {
  coverage: {
    approvedPrompts: number;
    neverRun: number;
    runBefore: number;
    oldestRunAt: string | null;
  };
  engines: string[];
  estimatedCaptures: number;
  prompts: {
    promptVersionId: string;
    persona: Persona;
    text: string;
    lastRunAt: string | null;
    neverRun: boolean;
  }[];
}

/**
 * FR-CAP-02 — trigger a run for a whole pillar, which is how a row of the
 * Narrative Alignment Matrix gets filled.
 *
 * Two things are visible before the click, because Rakesh asked the right
 * question about a 50-prompt pillar: WHICH prompts will run, and how much of
 * the pillar has ever been covered. A scan control that silently picks an
 * arbitrary subset looks like coverage and isn't — and without last-run dates
 * there is no way to tell a fresh scan from re-running the same three prompts.
 *
 * The cost is shown up front too. Each prompt × engine pair is one billable
 * capture and one scoring pass, so an unbounded "scan everything" button is a
 * way to spend real money by accident (NFR.md §7).
 */
function RunScan({
  brandId,
  pillars,
  onStarted,
}: {
  brandId: string | null;
  pillars: { pillarId: string; name: string }[];
  onStarted: () => void;
}) {
  const [pillarId, setPillarId] = useState("");
  const [maxPrompts, setMaxPrompts] = useState(3);
  const [preview, setPreview] = useState<ScanPreview | null>(null);
  const [showPrompts, setShowPrompts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pillarId && pillars[0]) setPillarId(pillars[0].pillarId);
  }, [pillars, pillarId]);

  // The preview is computed by the same function the run uses, so what is
  // listed here is exactly what will execute — not a description of it.
  useEffect(() => {
    if (!pillarId) return;
    setPreview(null);
    void api<ScanPreview>(
      `/pillars/${pillarId}/scan-preview?maxPrompts=${maxPrompts}`,
    )
      .then(setPreview)
      .catch(() => setPreview(null));
  }, [pillarId, maxPrompts]);

  async function start() {
    if (!brandId || !pillarId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api<{
        promptsStarted: number;
        coverage: ScanPreview["coverage"];
        estimatedCaptures: number;
        captureMode: string;
      }>(`/pillars/${pillarId}/runs`, { method: "POST", body: { maxPrompts } });
      const left = res.coverage.neverRun - res.promptsStarted;
      setResult(
        `Started ${res.promptsStarted} prompt${res.promptsStarted === 1 ? "" : "s"} (${res.estimatedCaptures} captures, ${res.captureMode} mode). ${
          left > 0
            ? `${left} prompt${left === 1 ? "" : "s"} in this pillar still have never been run — scan again to work through them.`
            : "Every approved prompt in this pillar has now been run at least once."
        }`,
      );
      onStarted();
      void api<ScanPreview>(
        `/pillars/${pillarId}/scan-preview?maxPrompts=${maxPrompts}`,
      )
        .then(setPreview)
        .catch(() => undefined);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not start the scan.",
      );
    } finally {
      setBusy(false);
    }
  }

  const coverage = preview?.coverage;
  const captures = preview?.estimatedCaptures ?? 0;

  return (
    <div className="section">
      <div className="section__head">
        <div>
          <h2 className="section__title">Run a scan</h2>
          <p className="section__lede">
            Ask the engines a pillar&rsquo;s approved prompts now. Prompts that
            have never been run are picked first, then the least recently run —
            so scanning again works through the pillar instead of repeating
            itself.
          </p>
        </div>
      </div>

      <div className="filterbar">
        <label className="filterbar__group">
          <span>Narrative Pillar</span>
          <select
            value={pillarId}
            onChange={(e) => setPillarId(e.target.value)}
          >
            {pillars.map((p) => (
              <option key={p.pillarId} value={p.pillarId}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="filterbar__group">
          <span>Prompts</span>
          <select
            value={maxPrompts}
            onChange={(e) => setMaxPrompts(Number(e.target.value))}
          >
            {[1, 3, 5, 10, 25].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <span className="filterbar__spacer" />
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={
            busy || !pillarId || !preview || preview.prompts.length === 0
          }
          onClick={() => void start()}
        >
          {busy ? "Starting…" : "Run scan"}
        </button>
      </div>

      {coverage ? (
        <div className="coverage">
          <span className="coverage__stat">
            <strong>{coverage.approvedPrompts}</strong> approved prompts
          </span>
          <span className="coverage__stat">
            <strong
              className={
                coverage.neverRun > 0 ? "numeric--critical" : "numeric--good"
              }
            >
              {coverage.neverRun}
            </strong>{" "}
            never run
          </span>
          <span className="coverage__stat">
            <strong>{coverage.runBefore}</strong> run before
            {coverage.oldestRunAt
              ? ` · oldest ${new Date(coverage.oldestRunAt).toLocaleDateString()}`
              : ""}
          </span>
          <span className="coverage__stat">
            This scan: <strong>{captures}</strong> capture
            {captures === 1 ? "" : "s"} · about $
            {((captures * 1.5) / 1000).toFixed(2)}
          </span>
          <span className="filterbar__spacer" />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setShowPrompts((v) => !v)}
            aria-expanded={showPrompts}
          >
            {showPrompts ? "Hide" : "Show"} the {preview.prompts.length} prompt
            {preview.prompts.length === 1 ? "" : "s"} this will run
            <Icon name="chevronDown" />
          </button>
        </div>
      ) : null}

      {showPrompts && preview ? (
        <ul className="scanlist">
          {preview.prompts.map((p) => (
            <li className="scanlist__item" key={p.promptVersionId}>
              <span className="badge badge--neutral">
                {PERSONA_LABELS[p.persona] ?? p.persona}
              </span>
              <span className="scanlist__text">{p.text}</span>
              <span
                className={`badge ${p.neverRun ? "badge--info" : "badge--neutral"}`}
              >
                {p.neverRun
                  ? "Never run"
                  : `Last run ${new Date(p.lastRunAt!).toLocaleDateString()}`}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {result ? <div className="alert alert--info">{result}</div> : null}
      {error ? <div className="alert alert--error">{error}</div> : null}
    </div>
  );
}

function RunPanel({
  runId,
  pillarName,
  onClose,
}: {
  runId: string;
  pillarName: (id: string) => string;
  onClose: () => void;
}) {
  const [run, setRun] = useState<RunDetail | null>(null);
  const [responses, setResponses] = useState<ResponseRow[] | null>(null);

  useEffect(() => {
    void api<RunDetail>(`/runs/${runId}`)
      .then(setRun)
      .catch(() => setRun(null));
    void api<{ items: ResponseRow[] }>(`/runs/${runId}/responses`)
      .then((d) => setResponses(d.items))
      .catch(() => setResponses([]));
  }, [runId]);

  return (
    <SidePanel
      open
      title="Capture run"
      subtitle={
        run ? (
          <>
            <span className={`badge ${STATUS_BADGE[run.status]}`}>
              {STATUS_LABELS[run.status]}
            </span>
            <span className="badge badge--neutral">
              {TRIGGER_LABELS[run.triggeredBy]}
            </span>
            <span>{pillarName(run.pillarId)}</span>
          </>
        ) : null
      }
      onClose={onClose}
    >
      <Tabs
        urlSync={false}
        compact
        ariaLabel="Run detail"
        tabs={[
          {
            id: "prompt",
            label: "Prompt sent",
            icon: "prompts",
            render: () =>
              run ? (
                <section className="evidence">
                  <h3 className="evidence__label">
                    Prompt sent to the engines
                  </h3>
                  {/* Shown verbatim on purpose: this is the exact text that left
                      the platform, and being able to check it is what makes the
                      "no Ground Truth in an outbound prompt" rule auditable
                      rather than asserted. */}
                  <p className="evidence__quote">{run.promptText}</p>
                  <p className="muted">
                    Nothing else was transmitted. Ground Truth is never included
                    in a prompt sent to a public engine — the score has to
                    reflect what the engine independently believes.
                  </p>
                </section>
              ) : (
                <div className="skeleton" style={{ height: 72 }} />
              ),
          },
          {
            id: "answers",
            label: "Answers",
            icon: "inbox",
            badge: responses?.length || null,
            render: () =>
              !responses ? (
                <div className="skeleton-stack">
                  <div className="skeleton" />
                  <div className="skeleton" />
                </div>
              ) : responses.length === 0 ? (
                <p className="muted">
                  No engine results recorded for this run yet.
                </p>
              ) : (
                <>
                  {responses.map((response) => (
                    <section className="evidence" key={response.responseId}>
                      <h3 className="evidence__label">
                        {ENGINE_LABELS[response.engine] ?? response.engine}
                        <span
                          className={`badge ${response.status === "success" ? "badge--aligned" : "badge--critical"}`}
                        >
                          {response.status}
                        </span>
                      </h3>
                      {response.status === "failed" ? (
                        <p className="evidence__none">
                          <Icon name="alert" />
                          {response.failureReason ??
                            "This engine failed and gave no reason."}
                        </p>
                      ) : (
                        <>
                          <pre className="run__answer">{response.rawText}</pre>
                          {response.citations.length > 0 ? (
                            <ul className="run__citations">
                              {response.citations.map((c) => (
                                <li key={c.citationId}>
                                  {c.url ? (
                                    <a
                                      href={c.url}
                                      target="_blank"
                                      rel="noreferrer noopener"
                                    >
                                      {c.sourceName || c.url}
                                    </a>
                                  ) : (
                                    (c.sourceName ?? "Unnamed source")
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="muted">
                              This engine exposed no citations for this answer.
                            </p>
                          )}
                        </>
                      )}
                    </section>
                  ))}
                </>
              ),
          },
        ]}
      />
    </SidePanel>
  );
}
